"use client"

import React, { useEffect, useRef, useState } from 'react';
import Loader from '../common/Loader';
import { DropiIntegrationInterface, getDropiIntegrations, postDropiIntegration } from '../../services/integrations/integrationsService';
import IntegrationsCard from './integrationsCard/IntegrationsCard';
import { dictionary } from '../../utils/dictionary';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { getAllPaymentMethods, getPaymentMethodConfiguration, postRegisterSellerApi, updatePaymentMethodActive, updatePaymentMethodInactive } from '../../services/payment/payment';
import { decodeTokenService } from '../../services/decodeTokenService';
import { PaymentMethod, PaymentMethodStatus } from '../payment/PaymentMethods/PaymentMethodsInterface';
import ModalDropiIntegration from './Modals/ModalDropiIntegration';
import ModalMercadoPagoIntegration from './Modals/ModalMercadoPagoIntegration';

interface dropiIntegrationInterface {
  created_at: string;
  id: number;
  integration_token: string;
  is_active: boolean;
  owner_id: string;
  updated_at: string;
}

const IntegrationsPanel: React.FC = () => {
  const [integrationsDropi, setIntegrationsDropi] = useState<dropiIntegrationInterface>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMercadoPago, setLoadingMercadoPago] = useState<boolean>(false);
  const [modalOpenDropi, setModalOpenDropi] = useState<boolean>(false);
  const [modalOpenMercadoPago, setModalOpenMercadoPago] = useState<boolean>(false);
  const [tokenDropi, setTokenDropi] = useState<string>('');
  const [tokenMercadoPagoPublic, setTokenMercadoPagoPublic] = useState<string>('');
  const [tokenMercadoPagoPrivate, setTokenMercadoPagoPrivate] = useState<string>('');
  const { language } = useAuth();
  const methodsStatus = useRef<PaymentMethodStatus[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loadedDropi, setLoadedDropi] = useState<boolean>(false);
  const [loadedPaymentMethods, setLoadedPaymentMethods] = useState<boolean>(false);
  const [loadedPaymentConfig, setLoadedPaymentConfig] = useState<boolean>(false);

  useEffect(() => {
    setLoadedDropi(false);
    getDropiIntegrations()
      .then((res) => {
        setIntegrationsDropi(res);
      }).catch((err) => {
        console.error(err);
        // toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
      })
      .finally(() => {
        setLoadedDropi(true);
      });
  }, []);

  const handlePostDropiIntegration = (token: string) => {
    setLoading(true);
    const data: DropiIntegrationInterface = {
      integration_token: token,
      is_active: true,
    }

    postDropiIntegration(data)
      .then((res) => {
        setModalOpenDropi(false);
        setTokenDropi('');
        setLoading(false);
        setIntegrationsDropi(res);
        toast.success(dictionary["integration-added"][language]);
      })
      .catch((err) => {
        toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
        setLoading(false);
      })
  }

  const handlePostMercadoPagoIntegration = (tokenPublic: string, tokenPrivate: string, id: string) => {
    setLoadingMercadoPago(true);
    const data = {
      public_key: tokenPublic,
      token: tokenPrivate,
    }

    postRegisterSellerApi(id, data)
      .then((res) => {
        updatePaymentMethodActive('mercadopago')
          .then((res) => {
            toast.success(dictionary["integration-added"][language]);
          })
          .catch((err) => {
            toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
          })
      })
      .catch((err) => {
        toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
      })
      .finally(() => {
        setModalOpenMercadoPago(false);
        setTokenMercadoPagoPublic('');
        setTokenMercadoPagoPrivate('');
        setLoadingMercadoPago(false);
        window.location.reload();
      });
  }

  const handleCancelDropiIntegration = () => {
    setLoading(true);
    const data: DropiIntegrationInterface = {
      is_active: false,
      integration_token: '',
    }

    postDropiIntegration(data)
      .then((res) => {
        setModalOpenDropi(false);
        setTokenDropi('');
        setIntegrationsDropi(res);
        toast.success(dictionary["integration-canceled"][language]);
        setLoading(false);

      })
      .catch((err) => {
        toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
        setLoading(false);
      })
  }


  const handleCancelMercadoPagoIntegration = () => {
    setLoadingMercadoPago(true);
    updatePaymentMethodInactive('mercadopago')
      .then((res) => {
        toast.success(dictionary["integration-canceled"][language]);
      })
      .catch((err) => {
        toast.error(dictionary["integration-error"][language] + ": " + dictionary[err.response.data.trigger][language]);
      })
      .finally(() => {
        setLoadingMercadoPago(false);
        window.location.reload();
      });
  }

  const handleGetMethods = async () => {
    setLoadedPaymentMethods(false);
    getAllPaymentMethods()
      .then((res) => {
        setMethods(res);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadedPaymentMethods(true);
      })
  };

  useEffect(() => {
    const getMethods = async () => {
      const userId = decodeTokenService.getUserIdFromToken() || "";
      await handleGetMethods();
      setLoadedPaymentConfig(false);
      getPaymentMethodConfiguration()
        .then((res) => {
          methodsStatus.current = res;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoadedPaymentConfig(true);
        });
    }
    getMethods();
  }, []);

  const initialLoading = !loadedDropi || !loadedPaymentMethods || !loadedPaymentConfig;

  if (loading || initialLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    )
  }

  const integrationsDefautl = [
    {
      id: 1,
      type: 'dropi',
      name: 'Dropi',
      logoSrc: 'https://dropi.co/wp-content/uploads/2025/04/Logo-dropi-Horizontal.svg',
      bgColor: 'bg-[#EB6F2D]',
      isActive: integrationsDropi?.is_active ?? false,
      isConnected: integrationsDropi?.is_active ?? false,
      onConnect: () => setModalOpenDropi(true),
      onCancel: () => handleCancelDropiIntegration(),
    },
    {
      id: 2,
      type: 'mercadopago',
      name: 'Mercado Pago',
      logoSrc: 'https://http2.mlstatic.com/frontend-assets/mp-web-navigation/ui-navigation/6.7.73/mercadopago/logo__large@2x.png',
      bgColor: 'bg-transparent',
      isActive: methodsStatus.current.find(method => method.id === 'mercadopago')?.active ?? false,
      isConnected: methodsStatus.current.find(method => method.id === 'mercadopago')?.active ?? false,
      onConnect: () => setModalOpenMercadoPago(true),
      onCancel: () => handleCancelMercadoPagoIntegration(),
    },
  ]

  // if (methodsStatus.current.length === 0) {
  //   return (
  //     <div className="flex justify-center items-center h-screen">
  //       <Loader />
  //     </div>
  //   )
  // }

  return (
    <div className="min-h-screen bg-gray-100 p-8 ml-4">
      <h1 className="text-2xl font-bold mb-4">{dictionary["integrations"][language]}</h1>
      <div className="flex gap-4">
        <div className='flex flex-wrap gap-4'>
          {integrationsDefautl.map((integration) => (
            <IntegrationsCard
              key={integration.id}
              name={integration.name}
              logoSrc={integration.logoSrc}
              isActive={integration.isActive}
              isConnected={integration.isConnected}
              onConnect={integration.onConnect}
              onCancel={integration.onCancel}
              bgColor={integration.bgColor}
            />
          ))}
        </div>
      </div>
      {modalOpenDropi && (
        <ModalDropiIntegration
          setModalOpenDropi={setModalOpenDropi}
          handlePostDropiIntegration={handlePostDropiIntegration}
          token={tokenDropi}
          setToken={setTokenDropi}
          language={language}
        />
      )}

      {modalOpenMercadoPago && (
        <ModalMercadoPagoIntegration
          id={'mercadopago'}
          setModalOpenMercadoPago={setModalOpenMercadoPago}
          handlePostMercadoPagoIntegration={handlePostMercadoPagoIntegration}
          tokenPublic={tokenMercadoPagoPublic}
          setTokenPublic={setTokenMercadoPagoPublic}
          tokenPrivate={tokenMercadoPagoPrivate}
          setTokenPrivate={setTokenMercadoPagoPrivate}
          loading={loadingMercadoPago}
          language={language}
        />
      )}
    </div>
  );
};

export default IntegrationsPanel;
